import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Copy, QrCode, Plus, Trash, FloppyDisk, Eye, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TenantReportFormBuilderPage() {
  const { fid } = useParams();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        API.get(`/tenant-report-forms/${fid}`),
        API.get(`/tenant-report-forms/${fid}/submissions`),
      ]);
      setForm(fRes.data);
      setSubmissions(sRes.data.submissions || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load form");
    } finally {
      setLoading(false);
    }
  }, [fid]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/tenant-report-forms/${fid}`, {
        name: form.name,
        welcome_text: form.welcome_text,
        branding: form.branding,
        categories: form.categories,
        custom_fields: form.custom_fields,
        severity_copy: form.severity_copy,
        active: form.active,
        property_address: form.property_address,
        mode: form.mode,
        client_company: form.client_company,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/report-issue/${form.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  if (loading || !form) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const publicUrl = `${window.location.origin}/report-issue/${form.token}`;

  const updateCat = (i, value) => {
    const next = [...form.categories];
    next[i] = value;
    setForm({ ...form, categories: next });
  };

  const updateField = (i, patch) => {
    const next = [...form.custom_fields];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, custom_fields: next });
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="trf-builder-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/tenant-report-forms" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-4 w-4" /> Back to forms
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{form.name}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {form.is_generic ? (
              <span className="inline-flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 font-medium">GENERIC</span>
                {form.client_company || "Direct tenants"}
              </span>
            ) : (
              form.client_company || form.client_name
            )}
            {" · "}
            {form.mode === "property" ? "Single property" : "All properties"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="trf-preview-btn">
              <Eye className="h-3.5 w-3.5 mr-1" /> Preview
            </Button>
          </a>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="trf-copy-link-btn">
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
          </Button>
          <a href={`${BACKEND}/api/tenant-report-forms/${fid}/qr`} target="_blank" rel="noreferrer" download={`${form.name}.png`}>
            <Button variant="outline" size="sm" data-testid="trf-download-qr-btn">
              <QrCode className="h-3.5 w-3.5 mr-1" /> QR
            </Button>
          </a>
          <Link to={`/tenant-report-forms/${fid}/poster`}>
            <Button variant="outline" size="sm" data-testid="trf-poster-btn">
              <Printer className="h-3.5 w-3.5 mr-1" /> Poster
            </Button>
          </Link>
          <Button onClick={save} disabled={saving} data-testid="trf-save-btn">
            <FloppyDisk className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Active toggle */}
      <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Form is {form.active ? "active" : "paused"}</p>
          <p className="text-xs text-slate-500">When paused, the public link returns "Form not found" and submissions are blocked.</p>
        </div>
        <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="trf-active-switch" />
      </div>

      {/* Welcome */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h2 className="text-lg font-semibold">Welcome message</h2>
        {form.is_generic && (
          <div>
            <Label>Public display name</Label>
            <Input value={form.client_company || ""}
                   onChange={(e) => setForm({ ...form, client_company: e.target.value })}
                   placeholder="e.g. Direct Tenants"
                   className="mt-1" data-testid="trf-builder-display-name" />
            <p className="text-[11px] text-slate-500 mt-1">Shown as the subtitle on the public form (e.g. underneath "Report a Maintenance Issue").</p>
          </div>
        )}
        <Textarea value={form.welcome_text || ""} onChange={(e) => setForm({ ...form, welcome_text: e.target.value })}
                  rows={3} className="text-sm" data-testid="trf-welcome-text" />
      </div>

      {/* Branding */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h2 className="text-lg font-semibold">Branding</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Logo URL</Label>
            <Input value={form.branding?.logo_url || ""}
                   onChange={(e) => setForm({ ...form, branding: { ...form.branding, logo_url: e.target.value } })}
                   placeholder="https://… (paste a public image URL)" className="mt-1"
                   data-testid="trf-logo-url" />
          </div>
          <div>
            <Label>Accent colour</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="color" value={form.branding?.accent_color || "#1D4ED8"}
                     onChange={(e) => setForm({ ...form, branding: { ...form.branding, accent_color: e.target.value } })}
                     className="w-16 h-10 cursor-pointer" data-testid="trf-accent-color" />
              <Input value={form.branding?.accent_color || "#1D4ED8"}
                     onChange={(e) => setForm({ ...form, branding: { ...form.branding, accent_color: e.target.value } })}
                     className="flex-1" />
            </div>
          </div>
        </div>
      </div>

      {/* Property mode */}
      {form.mode === "property" && (
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <h2 className="text-lg font-semibold">Linked property</h2>
          <p className="text-xs text-slate-500">Tenants don't need to type the address — pre-bound to the property below.</p>
          <Input value={form.property_address || ""}
                 onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                 placeholder="e.g. 14 Park Lane, London W1K 1QT" className="text-sm"
                 data-testid="trf-property-address" />
        </div>
      )}

      {/* Categories */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Issue categories</h2>
          <Button size="sm" variant="outline" onClick={() => setForm({ ...form, categories: [...(form.categories || []), "New category"] })}
                  data-testid="trf-add-cat-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(form.categories || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={c} onChange={(e) => updateCat(i, e.target.value)} className="text-sm" data-testid={`trf-cat-${i}`} />
              <button onClick={() => setForm({ ...form, categories: form.categories.filter((_, idx) => idx !== i) })}
                      className="text-red-500 hover:text-red-700 p-1.5" title="Remove">
                <Trash className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Severity helper text */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h2 className="text-lg font-semibold">Severity helper text</h2>
        <p className="text-xs text-slate-500">These guidance lines appear under each severity option to help tenants choose correctly.</p>
        <div>
          <Label className="text-red-700">Critical</Label>
          <Textarea rows={2} className="mt-1 text-sm" value={form.severity_copy?.critical || ""}
                    onChange={(e) => setForm({ ...form, severity_copy: { ...form.severity_copy, critical: e.target.value } })}
                    data-testid="trf-sev-critical" />
        </div>
        <div>
          <Label className="text-amber-700">Urgent</Label>
          <Textarea rows={2} className="mt-1 text-sm" value={form.severity_copy?.urgent || ""}
                    onChange={(e) => setForm({ ...form, severity_copy: { ...form.severity_copy, urgent: e.target.value } })}
                    data-testid="trf-sev-urgent" />
        </div>
        <div>
          <Label className="text-blue-700">Non-urgent</Label>
          <Textarea rows={2} className="mt-1 text-sm" value={form.severity_copy?.non_urgent || ""}
                    onChange={(e) => setForm({ ...form, severity_copy: { ...form.severity_copy, non_urgent: e.target.value } })}
                    data-testid="trf-sev-non_urgent" />
        </div>
      </div>

      {/* Custom fields */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Custom intake fields</h2>
          <Button size="sm" variant="outline"
                  onClick={() => setForm({ ...form, custom_fields: [...(form.custom_fields || []), { label: "Flat number", type: "text", required: false, options: [] }] })}
                  data-testid="trf-add-field-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add field
          </Button>
        </div>
        {(form.custom_fields || []).length === 0 && (
          <p className="text-xs text-slate-500">None — add fields like "Flat number" or "Best contact time" if you need them.</p>
        )}
        {(form.custom_fields || []).map((cf, i) => (
          <div key={i} className="border border-border rounded-md p-3 space-y-2 bg-muted/20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input value={cf.label} onChange={(e) => updateField(i, { label: e.target.value })}
                     placeholder="Label" data-testid={`trf-field-label-${i}`} />
              <select value={cf.type} onChange={(e) => updateField(i, { type: e.target.value })}
                      className="border border-border rounded-md px-2 text-sm bg-background"
                      data-testid={`trf-field-type-${i}`}>
                <option value="text">Single-line text</option>
                <option value="textarea">Multi-line text</option>
                <option value="select">Dropdown</option>
              </select>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!cf.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                Required
              </label>
            </div>
            {cf.type === "select" && (
              <Input value={(cf.options || []).join(", ")}
                     onChange={(e) => updateField(i, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                     placeholder="Options, comma-separated"
                     className="text-xs" data-testid={`trf-field-options-${i}`} />
            )}
            <div className="text-right">
              <button onClick={() => setForm({ ...form, custom_fields: form.custom_fields.filter((_, idx) => idx !== i) })}
                      className="text-red-500 hover:text-red-700 text-xs">Remove field</button>
            </div>
          </div>
        ))}
      </div>

      {/* Submissions */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold">Submissions ({submissions.length})</h2>
        {submissions.length === 0 ? (
          <p className="text-xs text-slate-500">No reports yet. Share the public link or QR with tenants to start collecting issues.</p>
        ) : (
          <div className="space-y-2">
            {submissions.map((s) => {
              const route = s.routed_to === "emergency_job" ? "/emergency-jobs/" : s.routed_to === "job" ? "/jobs/" : "/job-requests/";
              const sevColor = s.severity === "critical" ? "bg-red-100 text-red-700" : s.severity === "urgent" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-700";
              return (
                <Link key={s.id} to={`${route}${s.routed_id}`}
                      className="block border border-border rounded-md p-3 hover:bg-muted/30 transition"
                      data-testid={`trf-submission-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sevColor}`}>
                          {(s.severity || "").replace("_", " ").toUpperCase()}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">{s.reference}</span>
                        <span className="text-[10px] text-muted-foreground">→ {s.routed_reference}</span>
                      </div>
                      <p className="font-medium text-sm">{s.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.tenant_name} · {s.tenant_phone} · {s.category}</p>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
